import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

export interface HeroSlide {
  id: number;
  title: string;
  year: number;
  rating: number;
  genres: string[];
  region: string;
  backdrop: string;
  synopsis: string;
  type: 'movie' | 'tv';
}

interface HeroCarouselProps {
  slides: HeroSlide[];
}

export default function HeroCarousel({ slides }: HeroCarouselProps) {
  const [active, setActive] = useState(0);
  const touchStartX = useRef<number | null>(null);

  const goTo = useCallback(
    (index: number) => {
      const total = slides.length;
      setActive(((index % total) + total) % total);
    },
    [slides.length],
  );

  useEffect(() => {
    if (slides.length <= 1) return undefined;
    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [slides.length]);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      goTo(active + (delta < 0 ? 1 : -1));
    }
    touchStartX.current = null;
  };

  if (slides.length === 0) return null;

  return (
    <section
      className="relative w-full h-[560px] md:h-[660px] overflow-hidden bg-primary-950"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      aria-label="热门影视焦点图"
    >
      {slides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === active ? 'opacity-100 z-10' : 'opacity-0 z-0'
          }`}
          aria-hidden={index !== active}
        >
          <img
            src={slide.backdrop}
            alt={`${slide.title} 焦点海报`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/60 to-foreground-950/20" />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-transparent to-foreground-950/40" />
        </div>
      ))}

      <div className="relative z-20 h-full w-full px-4 md:px-8">
        <div className="h-full max-w-2xl flex flex-col justify-center pb-16 md:pb-20">
          {slides.map((slide, index) => (
            <div
              key={slide.id}
              className={index === active ? 'block animate-fade-up' : 'hidden'}
            >
              <div className="inline-flex items-center gap-2 rounded-full bg-accent-500/15 border border-accent-500/40 px-3 py-1 text-xs font-label font-medium text-accent-400">
                <i className="ri-fire-fill" />
                热门推荐
              </div>
              <h2 className="mt-4 font-heading text-3xl md:text-5xl font-extrabold text-background-50 leading-tight">
                {slide.title}
              </h2>
              <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-background-200">
                <span className="inline-flex items-center gap-1 font-label font-semibold text-accent-400">
                  <i className="ri-star-fill" />
                  {slide.rating.toFixed(1)}
                </span>
                <span className="font-label">{slide.year}</span>
                <span>{slide.region}</span>
                <span className="px-2 py-0.5 rounded border border-background-50/30 text-xs">
                  {slide.type === 'tv' ? '电视剧' : '电影'}
                </span>
                <span className="hidden sm:inline">{slide.genres.join(' · ')}</span>
              </div>
              <p className="mt-4 max-w-xl text-sm md:text-base text-background-200 leading-relaxed line-clamp-3">
                {slide.synopsis}
              </p>
              <div className="mt-7 flex flex-wrap items-center gap-3">
                <Link
                  to={`/detail/${slide.id}`}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-foreground-950 hover:bg-accent-400 transition-colors"
                >
                  <i className="ri-play-fill text-lg" />
                  立即观看
                </Link>
                <Link
                  to={`/detail/${slide.id}`}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 bg-foreground-950/20 px-6 py-3 text-sm font-medium text-background-50 hover:bg-background-50/10 transition-colors backdrop-blur"
                >
                  <i className="ri-information-line text-lg" />
                  查看详情
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>

      {slides.length > 1 && (
        <>
          <button
            type="button"
            onClick={() => goTo(active - 1)}
            aria-label="上一张"
            className="hidden md:flex absolute z-30 left-4 md:left-6 top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center rounded-full bg-foreground-950/40 text-background-50 hover:bg-accent-500 hover:text-foreground-950 transition-colors"
          >
            <i className="ri-arrow-left-s-line text-2xl" />
          </button>
          <button
            type="button"
            onClick={() => goTo(active + 1)}
            aria-label="下一张"
            className="hidden md:flex absolute z-30 right-4 md:right-6 top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center rounded-full bg-foreground-950/40 text-background-50 hover:bg-accent-500 hover:text-foreground-950 transition-colors"
          >
            <i className="ri-arrow-right-s-line text-2xl" />
          </button>
          <div className="absolute z-30 bottom-6 md:bottom-8 left-4 md:left-8 flex items-center gap-2">
            {slides.map((slide, index) => (
              <button
                key={slide.id}
                type="button"
                onClick={() => goTo(index)}
                aria-label={`切换到第 ${index + 1} 张`}
                className={`h-1.5 rounded-full transition-all ${
                  index === active ? 'w-8 bg-accent-500' : 'w-3 bg-background-50/50 hover:bg-background-50/80'
                }`}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}